import React, { useEffect, useState, useRef, useCallback } from "react";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight, /* Smartphone, */ Building2, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 10;
const MIN_BALANCE = 100;

const RedeemCash = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: customer, loading } = useSelector((state) => state.customer);

  const [activeTab, setActiveTab] = useState("earned");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [showMinBalPopup, setShowMinBalPopup] = useState(false);
  const loaderRef = useRef(null);

  useEffect(() => {
    const customerCode = localStorage.getItem("customercode");
    if (!customer) dispatch(fetchCustomerData(customerCode));
  }, []);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeTab]);

  const hasBankInfo = customer?.customer?.bankInfo && customer.customer.bankInfo.length > 0;

  const availablePoints = customer?.walletsDetail?.TotalavailableValue;
  const totalEarned = customer?.walletsDetail?.earnedValue;
  const totalTransferred = customer?.walletsDetail?.redeemValue;

  const earnedTxs =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher")
      ?.map(item => ({
        id: item.id,
        code: item.voucherCode,
        amount: item.earnedValue,
        isUsed: item.VoucherUsed,
        date: item.earnedDate,
      }))
      ?.sort((a, b) => new Date(b.date) - new Date(a.date)) || [];

  const transferredTxs =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher" && item.VoucherUsed === true)
      ?.map(item => ({
        id: item.id,
        code: item.voucherCode,
        amount: item.earnedValue,
        isUsed: true,
        date: item.updatedDate,
      }))
      ?.sort((a, b) => new Date(b.date) - new Date(a.date)) || [];

  const activeTxs = activeTab === "earned" ? earnedTxs : transferredTxs;
  const visibleTxs = activeTxs.slice(0, visibleCount);
  const hasMore = visibleCount < activeTxs.length;

  const handleObserver = useCallback((entries) => {
    if (entries[0].isIntersecting && hasMore) {
      setVisibleCount(prev => prev + PAGE_SIZE);
    }
  }, [hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  // ✅ Common check — balance 0 se jyada ho to hi redeem allow karo
  const checkBalanceAndProceed = (onSuccess) => {
    if (!availablePoints || availablePoints <= 0) {
      toast({
        title: t("common.failed"),
        description: t("wallet.noBalance"),
        variant: "destructive",
      });
      return;
    }
    onSuccess();
  };

  const handleRedeem = () => {
    checkBalanceAndProceed(() => navigate("/bank-details"));
  };

  // UPI disabled for now
  // const handleUPIRedeem = () => {
  //   checkBalanceAndProceed(() =>
  //     navigate("/upi-details", { state: { selectedAmount: availablePoints } })
  //   );
  // };

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex justify-center">
      <div className="w-full max-w-md bg-white min-h-screen md:rounded-3xl md:shadow-lg">
     {loading  && (
          <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
            <Loader2 className="animate-spin h-12 w-12" style={{ color: "#1A1F36" }} />
          </div>
        )}
        {/* HEADER */}
        <div className="relative flex items-center justify-center border-b border-gray-100 px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full"
            style={{ backgroundColor: "#1A1F3610" }}
          >
            <ArrowLeft className="w-5 h-5 text-[#1A1F36]" />
          </button>
          <p className="text-base font-semibold text-[#1A1F36]">
            {t("redeem.title")}
          </p>
        </div>

        <div className="px-4 py-5 space-y-4">

          {/* BALANCE CARD */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-center mb-1 text-[#1A1F36]">
              {t("wallet.balance")}
            </p>
            <p className="text-[48px] font-bold text-[#1A1F36] text-center leading-tight mb-4">
              ₹{availablePoints}
            </p>
            <div className="flex justify-between">
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{t("wallet.totalEarned")}</p>
                <p className="text-[15px] font-semibold text-[#1A1F36]">₹{totalEarned}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{t("wallet.transferred")}</p>
                <p className="text-[15px] font-semibold text-[#1A1F36]">₹{totalTransferred}</p>
              </div>
            </div>
          </div>

          {/* TRANSFER FUNDS */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1">
            <p className="text-[15px] font-bold text-[#1A1F36] mb-4">{t("redeem.transferFunds")}</p>

            {/* UPI disabled for now — wapas lana ho to grid-cols-2 karke niche wala block uncomment karo */}
            <div className="grid grid-cols-1 gap-3">
              {/* UPI */}
              {/* <button
                onClick={handleUPIRedeem}
                className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl border-[1.5px] border-[rgb(210,215,230)] bg-[rgb(226,230,240)] active:scale-[0.97] transition-transform hover:bg-[rgb(210,215,230)]"
              >
                <div className="w-10 h-10 rounded-xl bg-[rgb(200,210,230)] flex items-center justify-center">
                  <Smartphone size={20} className="text-[rgb(26,31,54)]" />
                </div>
                <p className="text-[13px] font-semibold text-[rgb(26,31,54)]">{t("redeem.upi")}</p>
              </button> */}

              {/* Bank */}
              <button
                onClick={handleRedeem}
                className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl border-[1.5px] border-[rgb(210,215,230)] bg-[rgb(226,230,240)] active:scale-[0.97] transition-transform hover:bg-[rgb(210,215,230)]"
              >
                <div className="w-10 h-10 rounded-xl bg-[rgb(200,210,230)] flex items-center justify-center">
                  <Building2 size={20} className="text-[rgb(26,31,54)]" />
                </div>
                <p className="text-[13px] font-semibold text-[rgb(26,31,54)]">{t("redeem.bank")}</p>
              </button>
            </div>
          </div>

        </div>

        {/* ✅ Minimum Balance Popup */}
        {showMinBalPopup && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-[90%] max-w-sm rounded-2xl p-6 text-center relative">
              <button
                onClick={() => setShowMinBalPopup(false)}
                className="absolute top-3 right-3 text-gray-400 text-xl"
              >
                ✖
              </button>

              <div className="w-16 h-16 mx-auto rounded-full bg-yellow-100 flex items-center justify-center mb-4 text-2xl">
                ⚠️
              </div>

              <h2 className="text-lg font-bold text-[#1A1F36] mb-2">
                {t("redeem.minBalanceTitle", "Minimum Balance Required")}
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                {t("redeem.minBalanceDesc", {
                  min: MIN_BALANCE,
                  current: availablePoints,
                })}
              </p>

              <button
                onClick={() => setShowMinBalPopup(false)}
                className="w-full py-3 rounded-xl text-white font-semibold"
                style={{ backgroundColor: "#1A1F36" }}
              >
                {t("common.done")}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default RedeemCash;